import React, { useEffect, useState, useRef } from 'react';
import { useMyContext } from '../MyContext';
import { FaEdit, FaTrash, FaSave, FaTimes, FaClone } from 'react-icons/fa';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import Modal from 'react-modal';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import ConditionRuleBuilder from './ConditionRuleBuilder';

const ITEMS_PER_PAGE = 10;
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#A28EFF', '#FF6688', '#33D4B9'];

const apiGatewayUrl = 'https://m082qhj8m5.execute-api.us-east-1.amazonaws.com/prod';

const ClientScorecardTab = () => {
  const {
    clientConfig,
    selectedClientCode,
    setSelectedClientCode,
    clientCodes,
    promptMaster,
    addClientConfig,
    updateClientConfig,
    deleteClientConfig,
    fetchClientConfig,
    loading
  } = useMyContext();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [editIndex, setEditIndex] = useState(null);
  const [editScore, setEditScore] = useState('');
  const [cloneModalOpen, setCloneModalOpen] = useState(false);
  const [selectedCloneClient, setSelectedCloneClient] = useState('');
  const [isCloning, setIsCloning] = useState(false);
  const [selectedPrompts, setSelectedPrompts] = useState([]);
  const [dependencyRules, setDependencyRules] = useState([]);


  useEffect(() => {
    if (!selectedClientCode && clientCodes.length > 0) {
      setSelectedClientCode(clientCodes[0].clientcode);
    }
  }, [clientCodes]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedClientCode]);

  if (!selectedClientCode || !clientConfig) {
    return <p className="p-4">Loading client scorecard...</p>;
  }

  const promptCategories = clientConfig?.rulesset?.promptcategory || [];
  const filtered = promptCategories.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const currentItems = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  // Updated to exclude negative scores
  const totalScore = filtered.reduce((sum, item) => {
    const score = parseFloat(item.score || 0);
    return score >= 0 ? sum + score : sum;
  }, 0);

  // Updated to exclude negative scores
  const chartData = filtered
    .filter(item => parseFloat(item.score || 0) >= 0)
    .map(item => ({ name: item.name, value: parseFloat(item.score) }));

  const handleEdit = (index, item) => {
    setEditIndex(index);
    setEditScore(item.score);
  };

  const handleCancelEdit = () => {
    setEditIndex(null);
    setEditScore('');
  };

  const handleSaveEdit = async (categoryName) => {
    if (isNaN(editScore)) {
      alert('Score must be a number.');
      return;
    }
    await updateClientConfig(categoryName, parseFloat(editScore));
    setEditIndex(null);
    setEditScore('');
  };

  const handleDelete = async (categoryName) => {
    if (window.confirm(`Delete "${categoryName}" from client config?`)) {
      await deleteClientConfig(categoryName);
    }
  };

  const handleCloneClick = () => {
    setCloneModalOpen(true);
  };

  const handleClone = async () => {
    if (!selectedCloneClient) {
      alert('Please select a client to clone.');
      return;
    }

    setIsCloning(true);
    try {
      const response = await axios.get(`${apiGatewayUrl}/clientconfig?clientcode=${selectedCloneClient}`);
      const promptCategories = response.data?.rulesset?.promptcategory || [];

      for (const item of promptCategories) {
        await addClientConfig(item.name, item.score);
      }

      alert('Scorecard cloned successfully.');
      setCloneModalOpen(false);
      setSelectedCloneClient('');
    } catch (error) {
      console.error('Error cloning scorecard:', error);
      alert('Failed to clone scorecard.');
    } finally {
      setIsCloning(false);
    }
  };

  const PromptItem = ({ category }) => {
    const [, dragRef] = useDrag({ type: 'PROMPT', item: { category } });
    const isSelected = selectedPrompts.includes(category);

    const toggleSelect = () => {
      setSelectedPrompts(prev =>
        prev.includes(category) ? prev.filter(p => p !== category) : [...prev, category]
      );
    };

    return (
      <div
        ref={dragRef}
        onClick={toggleSelect}
        className={`p-2 m-1 border border-gray-400 rounded cursor-pointer text-sm ${
          isSelected ? 'bg-green-200' : 'bg-blue-100'
        }`}
      >
        {category}
      </div>
    );
  };

  const DropZone = ({ onDrop }) => {
    const [, dropRef] = useDrop({
      accept: 'PROMPT',
      drop: (item) => onDrop(item.category),
    });

    return (
      <div
        ref={dropRef}
        className="p-6 border-2 border-dashed border-gray-400 bg-gray-50 text-center text-gray-600"
      >
        Drag or click to select prompts above, then drop here to add
      </div>
    );
  };

  const moveItem = (fromIndex, toIndex) => {
    const updated = [...promptCategories];
    const [moved] = updated.splice(fromIndex + (currentPage - 1) * ITEMS_PER_PAGE, 1);
    updated.splice(toIndex + (currentPage - 1) * ITEMS_PER_PAGE, 0, moved);
    clientConfig.rulesset.promptcategory = updated;
  };

  const handleMultiDrop = () => {
    selectedPrompts.forEach(category => {
      const exists = promptCategories.find(p => p.name === category);
      if (!exists) {
        const defaultScore = promptMaster.find(p => p.promptcategory === category)?.defaultscore || 1;
        addClientConfig(category, defaultScore);
      }
    });
    setSelectedPrompts([]);
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="p-4">
        <h3 className="text-xl font-semibold mb-4">Client Scorecard</h3>

        <label className="block mb-2">Select Client:</label>
        <select
          className="mb-4 p-2 border border-gray-300 rounded"
          value={selectedClientCode}
          onChange={(e) => setSelectedClientCode(e.target.value)}
        >
          <option value="">-- Choose Client --</option>
          {clientCodes.map((client) => (
            <option key={client.clientcode} value={client.clientcode}>
              {client.name}
            </option>
          ))}
        </select>

        <input
          type="text"
          placeholder="Search prompt category..."
          className="mb-4 p-2 border border-gray-300 rounded w-full"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <div className="mb-4">
          <h4 className="font-semibold mb-2">Available Prompt Categories</h4>
          <div className="flex flex-wrap gap-2 mb-2">
            {promptMaster?.map((item, index) => (
              <PromptItem key={index} category={item.promptcategory} />
            ))}
          </div>
          {selectedPrompts.length > 0 && (
            <div className="mb-2">
              <button
                onClick={handleMultiDrop}
                className="bg-green-600 text-white px-3 py-1 rounded mr-2"
              >
                Add Selected
              </button>
              <button
                onClick={() => setSelectedPrompts([])}
                className="bg-gray-400 text-white px-3 py-1 rounded"
              >
                Clear Selection
              </button>
            </div>
          )}

          <ConditionRuleBuilder
              rules={dependencyRules}
              setRules={setDependencyRules}
               promptMaster={promptMaster}
          />


          <DropZone onDrop={(category) => {
            const exists = promptCategories.find((p) => p.name === category);
            if (exists) {
              alert(`"${category}" already added.`);
              return;
            }
            const defaultScore = promptMaster.find(p => p.promptcategory === category)?.defaultscore || 1;
            addClientConfig(category, defaultScore);
          }} />
        </div>

        <button
          onClick={handleCloneClick}
          className="mb-4 px-3 py-2 border border-gray-300 rounded text-blue-600 hover:text-blue-800"
        >
          <FaClone className="inline-block mr-1" /> Clone
        </button>

        {filtered.length > 0 ? (
          <>
            <table className="min-w-full bg-white border border-gray-300">
              <thead>
                <tr>
                  <th className="border px-4 py-2">Prompt Category</th>
                  <th className="border px-4 py-2">Score</th>
                  <th className="border px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((item, idx) => (
                  <ScorecardRow
                    key={item.name}
                    index={idx}
                    item={item}
                    isEditing={editIndex === idx}
                    editScore={editScore}
                    setEditScore={setEditScore}
                    onEdit={() => handleEdit(idx, item)}
                    onSave={() => handleSaveEdit(item.name)}
                    onCancel={handleCancelEdit}
                    onDelete={() => handleDelete(item.name)}
                    moveItem={moveItem}
                    currentPage={currentPage}
                  />
                ))}
              </tbody>
            </table>

            <div className="mt-4 flex justify-between items-center">
              <span className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              <div>
                <button
                  className="mr-2 px-3 py-1 border rounded"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((prev) => prev - 1)}
                >
                  Prev
                </button>
                <button
                  className="px-3 py-1 border rounded"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((prev) => prev + 1)}
                >
                  Next
                </button>
              </div>
            </div>

            <div className="mt-4 text-right font-semibold text-gray-800">
              Total Score: {totalScore}
            </div>

            <div className="mt-8">
              <h4 className="font-semibold mb-2">Visual Score Distribution</h4>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={chartData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    fill="#8884d8"
                    label
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </>
        ) : (
          <p className="text-sm text-gray-600">No prompt categories defined.</p>
        )}

        <Modal
          isOpen={cloneModalOpen}
          onRequestClose={() => setCloneModalOpen(false)}
          className="bg-white p-6 rounded shadow-md max-w-md mx-auto mt-20"
          overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-start"
        >
          <h3 className="text-lg font-semibold mb-4">Clone Scorecard From Another Client</h3>
          <select
            value={selectedCloneClient}
            onChange={(e) => setSelectedCloneClient(e.target.value)}
            className="w-full mb-4 p-2 border border-gray-300 rounded"
          >
            <option value="">-- Select Client --</option>
            {clientCodes.filter(c => c.clientcode !== selectedClientCode).map((client) => (
              <option key={client.clientcode} value={client.clientcode}>
                {client.name}
              </option>
            ))}
          </select>
          <div className="text-right">
            <button
              onClick={handleClone}
              className="bg-blue-600 text-white px-4 py-2 rounded mr-2"
            >
              Clone
            </button>
            <button
              onClick={() => setCloneModalOpen(false)}
              className="bg-gray-400 text-white px-4 py-2 rounded"
            >
              Cancel
            </button>
          </div>
        </Modal>
      </div>
    </DndProvider>
  );
};

const ScorecardRow = ({
  index,
  item,
  isEditing,
  editScore,
  setEditScore,
  onEdit,
  onSave,
  onCancel,
  onDelete,
  moveItem,
  currentPage
}) => {
  const ref = useRef(null);

  const [, drop] = useDrop({
    accept: 'ROW',
    hover(dragged) {
      if (dragged.index === index) return;
      moveItem(dragged.index, index);
      dragged.index = index;
    }
  });

  const [{ isDragging }, drag] = useDrag({
    type: 'ROW',
    item: { index },
    collect: monitor => ({
      isDragging: monitor.isDragging()
    })
  });

  drag(drop(ref));

  return (
    <tr ref={ref} className={isDragging ? 'opacity-40' : ''}>
      <td className="border px-4 py-2 cursor-move">{item.name}</td>
      <td className="border px-4 py-2">
        {isEditing ? (
          <input
            type="number"
            value={editScore}
            onChange={(e) => setEditScore(e.target.value)}
            className="w-20 p-1 border border-gray-300 rounded"
          />
        ) : (
          item.score
        )}
      </td>
      <td className="border px-4 py-2 flex gap-2">
        {isEditing ? (
          <>
            <button onClick={onSave} className="text-green-600" title="Save">
              <FaSave />
            </button>
            <button onClick={onCancel} className="text-gray-600" title="Cancel">
              <FaTimes />
            </button>
          </>
        ) : (
          <>
            <button onClick={onEdit} className="text-blue-600" title="Edit">
              <FaEdit />
            </button>
            <button onClick={onDelete} className="text-red-600" title="Delete">
              <FaTrash />
            </button>
          </>
        )}
      </td>
    </tr>
  );
};

export default ClientScorecardTab;

